/**
 * App.js
 * Talkroute
 *
 * Created by Vel Genov on 12/8/18
 * Copyright (c) 2018 Talkroute. All right reserved.
 */

 import HistoryIcon from "@resources/icons/main-nav-history.svg";
 import KeypadIcon from "@resources/icons/main-nav-keypad.svg";
 import MessageIcon from "@resources/icons/main-nav-messages.svg";
 import VoicemailIcon from "@resources/icons/main-nav-voicemail.svg";
 import MeetingsIcon from "@resources/icons/meeting.svg";
 import { createStyles, List, makeStyles } from "@material-ui/core";
 import os from "os";
 import React, { useEffect, useState } from "react";
 import { useDispatch } from "react-redux";
 import { matchPath, useHistory, useLocation } from 'react-router';
 import { closeConversation } from "../actions/conversationActions";
 import appConfig from "../config";
 import ipcConstants from "../constants/ipcConstants";
 import routes, { routeUrlBuilders } from "../constants/routes";
 import { domIds } from "../constants/testConstants";
 import { useAppSelector } from "../helpers/hooks/useAppSelector.hook";
 import { ConversationData } from "../services/dataClasses/ConversationData";
 import MenuItemBuilder from "./MenuItemBuilder";
import usePrevious from "@helpers/hooks/usePrevious.hook";
import { push } from "connected-react-router";

 const menuItems = {
   KEYPAD: "Keypad",
   CALL_HISTORY: "History",
   TEXT_MESSAGES: "Messages",
   TEXT_MESSAGE_DETAILS: "Message Details",
   VOICEMAIL: "Voicemail",
   // this line is a hasty fix due to refresh causing keypad to be selected when voicemails are open
   VOICEMAIL_DETAILS: "Voicemail",
   MEETINGS: "Meetings"
 };

 /**
  * Allows users to navigate to different components of the application - Call History, Voicemail, etc.
  */
 const AppNavigation: React.FC = () => {
   const unreadConversations = useAppSelector(state => state.conversations.unreadCount)
   const unreadVoicemails = useAppSelector(state => state.voicemails.unreadCount)
   const missedCallsCount = useAppSelector(state => state.callHistory.missedCount)
   const conversationDraft = useAppSelector<ConversationData>(state => state.conversations.conversationDraft)
   const unreadTotal = unreadConversations + unreadVoicemails + missedCallsCount;
   const impersonateUserId = useAppSelector(state => state.impersonateUserReducer.impersonateUserId);
   const [activeItem, setActiveItem] = useState('')
   const [firstRender, setFirstRender] = useState(true)
   const location = useLocation();
   const prevLocation = usePrevious(location);
   const selectedConversationId = useAppSelector(state => state.conversations.selectedConversationId)
  //  const locationMemo = useMemo(() => location, [location])
   const history = useHistory();
   const shouldDisableMenu = false;
   const shouldDisableMeetingsMenu = impersonateUserId ? true : false;
   const dispatch = useDispatch();
   const classes = useStyles();

   /**
    * Route to the correct item on refresh
    */
   const getActiveItem = (): string => {
     const currentLocation = location.pathname;
     const currentMenuItem = Object.keys(routes).find(
       (k) => routes[k] === currentLocation
     );

     const matchResults = matchPath(location.pathname, routes.TEXT_MESSAGE_SINGLE);

     if (matchResults?.isExact) {
      return menuItems.TEXT_MESSAGES
     }

     // On refresh if activeItem is Text Message Details return Text Messages and close details window
     const activeItem = currentMenuItem && Object.keys(menuItems).includes(currentMenuItem)
        ? currentMenuItem === menuItems.TEXT_MESSAGE_DETAILS
          ? menuItems.TEXT_MESSAGES
          : menuItems[currentMenuItem]
        : menuItems.KEYPAD;
      return activeItem;
   };

   const ipcRenderer = appConfig.electron?.ipcRenderer;
   /**
    * Called when a route has changed
    *
    * @param path the new route path
    */
   const onRouteChanged = (path: string) => {
     switch (path) {
       case routes.KEYPAD:
         setActiveItem(menuItems.KEYPAD);
         break;
       case routes.CALL_HISTORY:
         setActiveItem(menuItems.CALL_HISTORY);
         break;
       case routes.TEXT_MESSAGES:
         setActiveItem(menuItems.TEXT_MESSAGES);
         break;
       case routes.TEXT_MESSAGE_DETAILS:
         setActiveItem(menuItems.TEXT_MESSAGES);
         break;
       case routes.VOICEMAIL:
         setActiveItem(menuItems.VOICEMAIL);
         break;
       case routes.MEETINGS:
         setActiveItem(menuItems.MEETINGS);
         break;
       default:
     }
   };

   /**
    * Handles clicks on the menu items
    *
    * @param {*} name the name of the item that was clicked
    */
   const handleItemClick = (name: string) => {
    if (activeItem === name) return;

     setActiveItem(name);
     navigateToItem(name);
   };

   /**
    * Switches routes and navigates to clicked route
    *
    * @param name name of clicked navigation button
   */
   const navigateToItem = (name: string) => {
     switch (name) {
       case menuItems.KEYPAD: {
         history.push(routes.KEYPAD);
         break;
       }
       case menuItems.CALL_HISTORY: {
         history.push(routes.CALL_HISTORY);
         break;
       }
       case menuItems.TEXT_MESSAGES: {
         history.push(routes.TEXT_MESSAGES);
         break;
       }
       case menuItems.VOICEMAIL: {
         history.push(routes.VOICEMAIL);
         break;
       }
       case menuItems.MEETINGS: {
         history.push(routes.MEETINGS);
         break;
       }
       default: {
         history.push(routes.HOME);
         break;
       }
     }
   };

   /**
    * Updates the badge number of the app to the number of unread items
    *
    * @param unreadTotal the total number of unread items of the app
    */
   const updateBadge = (unreadTotal: number) => {
     if (!ipcRenderer) return;
     switch (os.platform()) {
       case "win32":
         ipcRenderer.send(ipcConstants.UPDATE_BADGE_WIN, unreadTotal, {
           background: "crimson",
         });
         break;
       case "darwin":
         ipcRenderer.send(ipcConstants.UPDATE_BADGE_MAC_LINUX, unreadTotal);
         break;
       case "linux":
         ipcRenderer.send(ipcConstants.UPDATE_BADGE_MAC_LINUX, unreadTotal);
         break;
       default:
         ipcRenderer.send(ipcConstants.UPDATE_BADGE_MAC_LINUX, unreadTotal);
         break;
     }
   };

   /**
    * Clear the conversation draft if it exists
    */
   const clearConversationDraft = (conversationDraftLocal: ConversationData) => {
     if (conversationDraftLocal !== null) dispatch(closeConversation());
   };

   /**
    * Called when the window is shown after hidden
    */
   const handleShowWindow = () => {
     updateBadge(unreadTotal);
   };

   useEffect(() => {
     if (firstRender) {
       const currentItem = getActiveItem()
       setActiveItem(currentItem)
       setFirstRender(false)
       return
     }

     if (ipcRenderer) {
       ipcRenderer.on(ipcConstants.SHOW_WINDOW, handleShowWindow);
     }
     
     if (prevLocation && location.pathname !== prevLocation.pathname) {
        onRouteChanged(location.pathname);
        if (prevLocation.pathname === routes.TEXT_MESSAGES &&
          location.pathname !== routeUrlBuilders.OPEN_CONVERSATION(selectedConversationId ?? '')) {
          clearConversationDraft(conversationDraft);
        }
     }

     // Set Clear Callback function
     return () => {
       updateBadge(0)
       if (ipcRenderer) {
         ipcRenderer.removeListener(
           ipcConstants.SHOW_WINDOW,
           handleShowWindow
         )
       }
     }
   })

   return (
     <List>
       <MenuItemBuilder
         id={domIds.NAVIGATION_KEYPAD_ITEM}
         item={menuItems.KEYPAD}
         IconSvg={<KeypadIcon className={classes.iconSizingKeypad} />}
         handleClick={handleItemClick}
         className={classes.itemBuilder}
         missedCount={0}
         activeItem={activeItem}
         shouldDisableMenu={shouldDisableMenu} />
       <MenuItemBuilder
         id={domIds.NAVIGATION_HISTORY_ITEM}
         item={menuItems.CALL_HISTORY}
         IconSvg={<HistoryIcon className={classes.iconSizingNormal}/>}
         handleClick={handleItemClick}
         className={classes.itemBuilder}
         missedCount={missedCallsCount}
         activeItem={activeItem}
         shouldDisableMenu={shouldDisableMenu} />
       <MenuItemBuilder
         id={domIds.NAVIGATION_MESSAGES_ITEM}
         item={menuItems.TEXT_MESSAGES}
         IconSvg={<MessageIcon className={classes.iconSizingNormal}/>}
         handleClick={handleItemClick}
         className={classes.itemBuilder}
         missedCount={unreadConversations}
         activeItem={activeItem}
         shouldDisableMenu={shouldDisableMenu} />
       <MenuItemBuilder
         id={domIds.NAVIGATION_VOICEMAIL_ITEM}
         item={menuItems.VOICEMAIL}
         IconSvg={<VoicemailIcon className={classes.iconSizingNormal}/>}
         handleClick={handleItemClick}
         className={classes.itemBuilder}
         missedCount={unreadVoicemails}
         activeItem={activeItem}
         shouldDisableMenu={shouldDisableMenu} />
       <MenuItemBuilder
         id={domIds.NAVIGATION_MEETINGS_ITEM}
         item={menuItems.MEETINGS}
         IconSvg={<MeetingsIcon className={classes.iconSizingNormal}/>}
         handleClick={handleItemClick}
         className={classes.itemBuilder}
         missedCount={0}
         activeItem={activeItem}
         shouldDisableMenu={shouldDisableMeetingsMenu} />
     </List>
   );
 }

 const useStyles = makeStyles(
   createStyles({
     itemBuilder: {
       padding: '1em 0'
     },
     iconSizingKeypad: {
       width: 21,
       height: 27,
     },
     iconSizingNormal: {
       width: 27,
       height: 27,
     }
   })
 )

 export default AppNavigation

